/**
 * LiveScanProvider — app-level singleton for the live Pokédex OCR scan session.
 *
 * WHY THIS EXISTS (2026-07 broadcast regression fix)
 * --------------------------------------------------
 * `useDexLiveScan` owns the ReplayKit broadcast, the App-Group frame poller,
 * the `onFinished` listener, and the PiP window. It used to be instantiated
 * PER COMPONENT — once inside HeroDexButton (which lives in the TabBar, and the
 * TabBar is re-created by every page's own <AppShell>) and again inside
 * DexLiveScanMenu (mounted only on /dex). So there were TWO independent copies
 * of the scan state over ONE global native broadcast, and every route change
 * unmounted a copy. The hook's teardown effect fires `stop(true)` on unmount,
 * and stop() re-taps the iOS broadcast picker whenever the *native* broadcast
 * is live — so navigating to Settings (or any page) mid-scan re-presented the
 * picker and layered a second poller/listener/PiP over the same frames. That is
 * the "pressing a menu fires off another broadcast instance" bug.
 *
 * The fix: run `useDexLiveScan` EXACTLY ONCE here, mounted ABOVE the per-page
 * <AppShell> (see App.tsx), and have every scan surface — the Hero medallion's
 * quick-start, the DexLiveScanMenu, banners — read that one session from
 * context. The hook now never unmounts on navigation, so its teardown only runs
 * at app exit; there is a single frame pump / listener / PiP; and every "start a
 * scan" entry point genuinely drives the same session. Mirrors the existing
 * BroadcastProvider / SessionProvider singleton pattern.
 *
 * VARIANT: each old instance took an `initialVariant` at mount (the dex pill the
 * user was viewing) as the fallback used when OCR can't pin the variant down. A
 * singleton can't take a per-consumer mount value, so it tracks the active pill
 * reactively via `useLastActiveVariant()` (the same source HeroDexButton used).
 * `useDexLiveScan` already re-syncs its internal fallback ref whenever
 * `initialVariant` changes, so switching pills updates the fallback exactly as
 * before.
 */
import { createContext, useContext, type ReactNode } from "react";
import {
  useDexLiveScan,
  type UseDexLiveScanReturn,
} from "@/hooks/useDexLiveScan";
import {
  useLastActiveVariant,
  listDexEntriesForVariant,
} from "@/lib/dexStateStore";
import {
  ALL_VARIANTS,
  type PokedexVariant,
} from "@/lib/pogoPokedexVariantClassifier";
import { hasProAccess, useSessionOptional } from "@/components/SessionProvider";

const LiveScanContext = createContext<UseDexLiveScanReturn | null>(null);
// The PHASE alone (2026-09-22, Galaxy S21+): the Dex page only needs to know
// whether a scan is running, and the full context value changes on every
// frame's totals / diagnostics update. A string context only notifies on a
// real phase change, so per-frame state never re-renders the 1,025-cell grid.
const LiveScanPhaseContext = createContext<string>("idle");

/**
 * Fallback variant when the user has no last-active pill recorded (a truly
 * fresh install). Returns the variant with the most recorded entries as a proxy
 * for "what they were last working on", defaulting to the main dex. This is the
 * last resort only: `resetLandingVariantToMain()` seeds "pokemon" at boot, so
 * `useLastActiveVariant()` is virtually always non-null in practice.
 */
function resolveDefaultVariant(): PokedexVariant {
  let best: PokedexVariant = "pokemon";
  let bestCount = -1;
  for (const v of ALL_VARIANTS) {
    try {
      const count = listDexEntriesForVariant(v).length;
      if (count > bestCount) {
        bestCount = count;
        best = v;
      }
    } catch {
      // dexStateStore unavailable (SSR/test env) — keep default.
    }
  }
  return best;
}

export function LiveScanProvider({ children }: { children: ReactNode }) {
  // Reactive: re-renders (and re-syncs the hook's fallback variant) whenever the
  // user switches dex pills, so the fallback tracks the pill exactly as the old
  // per-mount `initialVariant` did.
  const activeVariant = useLastActiveVariant();
  // Detail-page live scanning is Pro, matching the screenshot-upload path in
  // ScanPokedexModal. Read here rather than inside the hook so useDexLiveScan
  // stays free of session context; `useSessionOptional` keeps this provider
  // renderable in isolation (unit tests), where no session means no Pro.
  const sessionState = useSessionOptional();
  const session = useDexLiveScan({
    initialVariant: activeVariant ?? resolveDefaultVariant(),
    isPro: sessionState != null && hasProAccess(sessionState.tier),
  });
  return (
    <LiveScanContext.Provider value={session}>
      <LiveScanPhaseContext.Provider value={session.state.phase}>
        {children}
      </LiveScanPhaseContext.Provider>
    </LiveScanContext.Provider>
  );
}

/** The live scan's phase only; re-renders the caller on phase changes, never per frame. */
export function useLiveScanPhase(): string {
  return useContext(LiveScanPhaseContext);
}

export function useLiveScan(): UseDexLiveScanReturn {
  const ctx = useContext(LiveScanContext);
  if (!ctx) {
    throw new Error("useLiveScan must be used inside a LiveScanProvider");
  }
  return ctx;
}
